import { computed, watch } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { CLEAR_ERRORMSG, REQUEST_EMAIL_VERIFICATION } from "@/store/action-types";
import { DEFAULT_ERROR_MSG } from "@/utils/const";
import type { BucketPromptManualPayload } from "@/dtos/BucketWebhook.dto";

/**
 * Composable for app-level store access and error handling
 */
export function useAppStore() {
  const store = useStore();
  const route = useRoute();
  const $toast = useToast();

  const account = computed(() => store.getters.account?.data);
  const accountIsLoading = computed(() => store.getters.account?.isLoading);
  const errorMessage = computed(() => store.getters.errorMsg);
  const enrichmentBanner = computed(() => store.getters.enrichmentBanner);
  const callBucketPrompt = computed(() => store.getters.callBucketPrompt);

  watch(errorMessage, (val) => {
    if (val != null && $toast) {
      const isSubscriptionActiveError =
        typeof val === "string" && val.toLowerCase().includes("subscription/organization/active");

      if (!isSubscriptionActiveError) {
        $toast.error(val ?? DEFAULT_ERROR_MSG);
      }
      store.dispatch(CLEAR_ERRORMSG);
    }
  });

  const requestVerification = async () => {
    if (!accountIsLoading.value && $toast) {
      const res = await store.dispatch(REQUEST_EMAIL_VERIFICATION);
      if (res) {
        $toast.info("Verification email sent.");
      }
    }
  };

  const hideEnrichmentBanner = () => {
    store.commit("HIDE_ENRICHMENT_BANNER");
  };

  const showCallBucketPrompt = (payload: BucketPromptManualPayload) => {
    store.commit("SET_CALL_BUCKET_PROMPT", payload);
  };

  const hideCallBucketPrompt = () => {
    store.commit("HIDE_CALL_BUCKET_PROMPT");
  };

  return {
    account,
    accountIsLoading,
    errorMessage,
    enrichmentBanner,
    callBucketPrompt,
    route,
    requestVerification,
    hideEnrichmentBanner,
    showCallBucketPrompt,
    hideCallBucketPrompt,
  };
}
